import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Stack, TextField } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { API_KEY_MAX_NAME_LENGTH } from 'v2/constants';

const API_DETAILS_FORM_ID = 'api-details-step-form';
const STEP_INDEX = 0;

interface FormValues {
  name: string;
}

interface Props {
  onProceed: (name: string) => void;
  existingNames: string[];
}

const ApiDetailsStep: React.FC<Props> = ({ onProceed, existingNames }) => {
  const { translation } = useTranslation();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ mode: 'onTouched', defaultValues: { name: '' } });

  const onSubmit = handleSubmit((data) => {
    onProceed(data.name);
    goToNext();
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="api-details-step-submit"
          id="generate-api-key-button"
          variant="contained"
          type="submit"
          form={API_DETAILS_FORM_ID}
          endIcon={<ChevronRightIcon />}
        >
          {translation.generate_key}
        </Button>
      </Box>
    );
  }, [setFooterContent, steps, translation]);

  return (
    <Stack component="form" id={API_DETAILS_FORM_ID} onSubmit={onSubmit} noValidate gap={3}>
      <TextField
        {...register('name', {
          required: translation.name_cannot_be_empty,
          maxLength: {
            value: API_KEY_MAX_NAME_LENGTH,
            message: translation.api_key_name_max_length_error.replace('{{count}}', String(API_KEY_MAX_NAME_LENGTH))
          },
          validate: (value) => !existingNames.includes(value) || translation.name_must_be_unique
        })}
        id="input-name"
        label={translation.api_key_name}
        variant="outlined"
        error={!!errors.name}
        helperText={errors.name?.message}
        autoFocus
      />
    </Stack>
  );
};

export default ApiDetailsStep;
